// @file Dashboard navigation store
import { dashViewUrl } from '@@/bits/dash_helper'
import { __ } from '@@/bits/intl'
import { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import {
  OzTabsWithSeparatorButtonColorPreset,
  type OzTabsWithSeparatorButtonProps,
} from '@@/library/v4/components/OzTabsWithSeparator.vue'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import { useDashCollectionsStore } from '@@/pinia/dash_collections_store'
import { useDashLayoutPickerStore } from '@@/pinia/dash_layout_picker_store'
import { useDashStore } from '@@/pinia/dash_store'
import type { AccountMenuItem } from '@@/types'
import { MobilePage } from '@@/types/dash'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum DashTabs {
  HOME = 'Home',
  GALLERY = 'Gallery',
  JOIN = 'Join',
  MAKE = 'Make',
}

export const useDashNavigationStore = defineStore('dashNavigationStore', () => {
  const dashStore = useDashStore()
  const dashLayoutPickerStore = useDashLayoutPickerStore()
  const dashAccountsStore = useDashAccountsStore()
  const dashCollectionsStore = useDashCollectionsStore()

  // State
  const currentTabKey = ref<DashTabs>(DashTabs.HOME)

  // Getters
  const currentHomePath = computed<string>(() => {
    return dashAccountsStore.isCurrentAccountLibrary ? dashAccountsStore.currentLibraryDashboardPath : '/dashboard'
  })
  const tabs = computed<OzTabsWithSeparatorButtonProps[]>(() => [
    {
      key: DashTabs.HOME,
      text: __('Home'),
      href: generateHomeUrl(currentHomePath.value),
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: DashTabs.GALLERY,
      text: __('Gallery'),
      href: generateGalleryUrl(currentHomePath.value),
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
    },
    {
      key: DashTabs.JOIN,
      text: __('Join'),
    },
    {
      key: DashTabs.MAKE,
      text: __('+ Make'),
      href: generateMakeUrl(currentHomePath.value),
      hrefMode: OzBaseButtonHrefMode.HistoryPushState,
      colorPreset: OzTabsWithSeparatorButtonColorPreset.Primary,
      disabled:
        dashLayoutPickerStore.cannotMakeWall &&
        dashAccountsStore.currentLibrary != null &&
        !dashAccountsStore.isCurrentUserLibraryOwner,
    },
  ])

  // Actions
  const switchTab = (tabKey: DashTabs): void => {
    currentTabKey.value = tabKey

    switch (tabKey) {
      case DashTabs.HOME:
        // We need to replace gallery filter with recents to prevent showing old gallery page in Home page when switching from mobile viewport to desktop viewport.
        // TODO: Remove this block when building the UI for mobile viewport.
        if (dashCollectionsStore.activeCollectionKey.indexKey === 'gallery') {
          dashCollectionsStore.switchCollectionKey({ typeKey: 'filter', indexKey: 'combined_recents' })
        }
        dashStore.setCurrentMobilePage(MobilePage.AccountsMenu)
        break
      case DashTabs.GALLERY:
        dashStore.setCurrentMobilePage(MobilePage.CollectionsMenu)
        break
      case DashTabs.MAKE:
        dashStore.setCurrentMobilePage(MobilePage.LayoutPicker)
        break
    }
  }

  const generateHomeUrl = (prefixPath: string): string => {
    // We need to replace gallery filter with recents to prevent showing old gallery page in Home page when switching from mobile viewport to desktop viewport.
    // TODO: Remove this block when building the UI for mobile viewport.
    return dashViewUrl(
      {
        mobilePage: MobilePage.Collection,
        collectionKey:
          dashCollectionsStore.activeCollectionKey.indexKey !== 'gallery'
            ? dashCollectionsStore.activeCollectionKey
            : { typeKey: 'filter', indexKey: 'combined_recents' },
      },
      { path: prefixPath },
    )
  }

  const generateGalleryUrl = (prefixPath: string): string => {
    return dashViewUrl({ mobilePage: MobilePage.CollectionsMenu }, { path: `${prefixPath}/gallery` })
  }

  const generateMakeUrl = (prefixPath: string): string => {
    return dashViewUrl({ mobilePage: MobilePage.LayoutPicker }, { path: `${prefixPath}/make` })
  }

  const generateURLFromAccount = (account: AccountMenuItem): string => {
    const prefixPath = account.isLibrary ? account.url : '/dashboard'

    switch (currentTabKey.value) {
      case DashTabs.HOME:
        return generateHomeUrl(prefixPath)
      case DashTabs.GALLERY:
        return generateGalleryUrl(prefixPath)
      case DashTabs.MAKE:
        return generateMakeUrl(prefixPath)
      default:
        return dashViewUrl({ mobilePage: MobilePage.AccountsMenu })
    }
  }

  return {
    // State
    currentTabKey,

    // Getters
    tabs,

    // Actions
    switchTab,
    generateURLFromAccount,
  }
})
